import { useEffect, useId, useState } from "react";
import { invalidateLivePassport } from "../../../../lib/passport-refresh";
import { ApiError } from "../../../../lib/api";
import {
  getShoppingRange,
  saveShoppingRange,
  type ShoppingRangeState,
} from "../../../../lib/shopping-range";
import { isPropertyReadinessTarget } from "../../../../lib/readiness-target-display";
import type { ReadinessTarget } from "../../../../lib/readiness-types";
import type { UseTargetEditOptions } from "../../../../lib/profile/use-target-edit";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";

export function ShoppingRangeRow({
  target,
  opts,
  onRange,
  refreshVersion,
}: {
  target: ReadinessTarget | null | undefined;
  opts: UseTargetEditOptions;
  onRange: (state: ShoppingRangeState | null) => void;
  refreshVersion?: number;
}) {
  const inputId = useId();
  const [state, setState] = useState<ShoppingRangeState | null>(null);
  const [open, setOpen] = useState(false);
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [pending, setPending] = useState(false);
  const [readbackFailed, setReadbackFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await opts.getToken();
        if (!token) return;
        const data = await getShoppingRange(token);
        if (!cancelled) {
          setState(data);
          onRange(data);
        }
      } catch {
        /* Older servers do not expose this editor. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opts, onRange, refreshVersion]);

  if (!state) return null;
  const range = state.shopping_range;
  let display = "Not set";
  if (range) {
    display = `$${range.low.toLocaleString()}`;
    if (range.low !== range.high) {
      display += `–$${range.high.toLocaleString()}`;
    }
  }
  const midpointFollows =
    target?.price_source === "range_midpoint" && !isPropertyReadinessTarget(target);
  let saveEffect = "Saving this range will not create a target.";
  if (midpointFollows) {
    saveEffect = "Saving this range updates your target price to its midpoint.";
  } else if (target) {
    saveEffect = "Saving this range keeps your current target price.";
  }
  const save = async () => {
    if ((target?.id ?? null) !== state.base_target_id) {
      opts.onUncertain?.();
      setError("Your target changed. Refresh and review it before saving.");
      return;
    }
    if (readbackFailed) {
      setError(
        "Refresh this page to review the saved range before trying again.",
      );
      return;
    }
    const lowerPrice = Number(low);
    const upperPrice = Number(high);
    if (
      !Number.isInteger(lowerPrice) ||
      !Number.isInteger(upperPrice) ||
      lowerPrice < 100000 ||
      upperPrice > 5000000 ||
      lowerPrice > upperPrice
    ) {
      setError(
        "Enter whole dollar amounts between $100,000 and $5,000,000, with the lower amount first.",
      );
      return;
    }
    if (range?.low === lowerPrice && range.high === upperPrice) {
      setOpen(false);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const token = await opts.getToken();
      if (!token) throw new Error("Missing authentication");
      const next = await saveShoppingRange(token, state, lowerPrice, upperPrice);
      setState(next);
      onRange(next);
      setOpen(false);
      opts.onSuccess?.({ target: null, result: null });
    } catch (err) {
      // The write can have committed before a response failed. Read back and
      // require the buyer to review; never replay a stale draft automatically.
      try {
        const token = await opts.getToken();
        if (!token) throw new Error("Missing authentication", { cause: err });
        const latest = await getShoppingRange(token);
        setState(latest);
        onRange(latest);
        setReadbackFailed(false);
      } catch {
        setReadbackFailed(true);
      }
      opts.onUncertain?.();
      invalidateLivePassport();
      if (target) opts.onStaleRebase?.(target);
      setError(
        err instanceof ApiError && err.status === 409
          ? "Your target or range changed. Review the latest values before saving again."
          : "We couldn't confirm the save. Review the latest values before trying again.",
      );
    } finally {
      setPending(false);
    }
  };
  return (
    <article className="px-4 py-3 sm:px-5">
      <button
        type="button"
        className="flex min-h-12 w-full items-center justify-between text-left"
        aria-expanded={open}
        onClick={() => {
          if (!open) {
            setLow(range ? String(range.low) : "");
            setHigh(range ? String(range.high) : "");
            setError(null);
          }
          setOpen(!open);
        }}
      >
        <span className="font-semibold">Shopping range</span>
        <span>{display}</span>
      </button>
      {open ? (
        <div className="space-y-3 pb-2">
          <Input
            id={`${inputId}-low`}
            label="Lower price"
            type="number"
            inputMode="numeric"
            value={low}
            onChange={(e) => setLow(e.target.value)}
          />
          <Input
            id={`${inputId}-high`}
            label="Upper price"
            type="number"
            inputMode="numeric"
            value={high}
            onChange={(e) => setHigh(e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            {saveEffect}
          </p>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <Button loading={pending} onClick={() => void save()}>
            Save shopping range
          </Button>
        </div>
      ) : null}
    </article>
  );
}
